import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { SequenceRead } from '../../Util/orderSettingDetection/sequenceHead.ts'
import { readSequenceHead } from '../../Util/orderSettingDetection/sequenceHead.ts'
import type { ReadLayoutResult } from '../../Util/orderSettingDetection/readLayoutDetection.ts'
import { detectReadLayout } from '../../Util/orderSettingDetection/readLayoutDetection.ts'
import { normalizePrimerRegistry } from '../../Util/orderSettingDetection/primerRegistry.ts'
import type { SampleConfigurationGuess } from '../../Util/orderSettingDetection/sampleConfigurationClassifier.ts'
import { classifySampleConfiguration } from '../../Util/orderSettingDetection/sampleConfigurationClassifier.ts'
import type { RegistryRow } from './discovery.ts'
import { detectSequencingRuns } from '../../Util/orderSettingDetection/sequencingRunDetection.ts'
import type { ReadLengthSummary } from '../../Util/orderSettingDetection/readLengths.ts'
import { summarizeReadLengths } from '../../Util/orderSettingDetection/readLengths.ts'

/**
 * Must stay free of DOM, store and path-alias imports: it is bundled into the detection worker, which
 * gets neither the app's plugins nor its globals.
 */

export type DetectionFile = {
  key: string
  name: string
  size: number
  platform: DetectedSequencingPlatform
  /** Present for the files whose heads are read; the rest contribute their names only. */
  blob?: Blob
}

export type DetectionRequest = {
  files: DetectionFile[]
  registryRows: RegistryRow[] | null
  classify: boolean
}

export type DetectionResult = {
  /** Over every given file: names propose pairs, the heads that were read confirm or veto them. */
  layout: ReadLayoutResult
  guess: SampleConfigurationGuess | null
  filesScanned: number
  /** Runs named by the scanned heads; information only. */
  runs: { count: number }
  /** Over the scanned heads of files no mate pairs with; mates are not amplicon-length. */
  readLengths?: ReadLengthSummary
}

/** The classifier judges six files; two spare cover a negative control or an empty file among them. */
export const HEAD_FILES = 8

/** Name order keeps mates next to each other, so the scanned heads cover whole samples. */
export const filesToScan = <T extends { name: string }>(files: readonly T[]): T[] =>
  [...files].sort((a, b) => a.name.localeCompare(b.name)).slice(0, HEAD_FILES)

const runsOf = (files: { name: string; reads?: SequenceRead[] }[]): DetectionResult['runs'] => {
  const scanned = files.filter((file) => (file.reads?.length ?? 0) > 0)
  const result = detectSequencingRuns(scanned.map((file) => ({ name: file.name, headers: (file.reads ?? []).map((read) => read.header) })))
  return { count: result.runs.length }
}

export const runDetection = async (request: DetectionRequest): Promise<DetectionResult> => {
  const heads = await Promise.all(
    request.files.map((file): Promise<SequenceRead[] | undefined> => (file.blob ? readSequenceHead(file.blob) : Promise.resolve(undefined)))
  )
  const files = request.files.map((file, i) => ({ name: file.name, size: file.size, platform: file.platform, reads: heads[i] }))
  const registry = normalizePrimerRegistry(request.registryRows ?? [])
  const layout = detectReadLayout(files)
  const mated = new Set(layout.pairs.flat())
  const readLengths = summarizeReadLengths(
    files.flatMap((file, i) => (mated.has(i) ? [] : (file.reads ?? []).map((read) => read.sequence.length)))
  )
  return {
    layout,
    guess: request.classify ? classifySampleConfiguration(files, { registry }) : null,
    filesScanned: heads.filter((reads) => (reads?.length ?? 0) > 0).length,
    runs: runsOf(files),
    ...(readLengths ? { readLengths } : {})
  }
}
